import { useEffect, useRef } from 'react';

/**
 * Sets `document.title` for the lifetime of the calling component.
 *
 * Pass `null` (or omit) to install a baseline title that gets restored
 * on unmount — useful for the root route so we always end up with a
 * sensible title even if a child route forgets to set one. Pass a
 * string to override, and an optional `suffix` to brand it (e.g. `"Scout"`).
 *
 * The previous title is captured on mount and restored on unmount, so
 * transient routes don't pollute the tab when the user navigates back.
 */
export function useDocumentTitle(title: string | null | undefined, suffix: string | null = null): void {
	const previousTitleRef = useRef<string | null>(null);

	useEffect(() => {
		if (typeof document === 'undefined') return;
		previousTitleRef.current = document.title;

		return () => {
			const previous = previousTitleRef.current;
			if (previous !== null) {
				document.title = previous;
			}
		};
	}, []);

	useEffect(() => {
		if (typeof document === 'undefined') return;
		if (title === null || title === undefined || title.length === 0) return;
		document.title = suffix ? `${title} · ${suffix}` : title;
	}, [title, suffix]);
}
